import { createGlobalStyle, css, CSSProp } from "styled-components";

export const COLORS = {
  primary_light_blue: "#386097",

  primary_light_blue1: "#F2F8FD",
  primary_light_blue2: "#E5EFF9",
  primary_light_blue3: "#C5DFF2",
  primary_light_blue4: "#92C5E7",
  primary_light_blue5: "#6B94C5",
  primary_light_blue6: "#4876AF",

  primary_dark_blue7: "#2D4B77",
  primary_dark_blue8: "#284164",
  primary_dark_blue9: "#263854",
  primary_dark_blue10: "#192538",

  cirrus_black: "#282A31",
  cirrus_white: "#FFFFFF",
  cirrus_red: "#D52041",
  cirrus_red_1: "#E3415F",
  cirrus_red_error: "#FFE8E8",
  cirrus_dark_red: "#B12B44",
  cirrus_green: "#669E65",

  cirrus_black1: "#F7F8F8",
  cirrus_black2: "#EDEDF1",
  cirrus_black3: "#D7DAE0",
  cirrus_black4: "#B4B9C5",
  cirrus_black5: "#6B7285",

  cirrus_black6: "#6E7789",
  cirrus_black7: "#585F71",
  cirrus_black8: "#484D5C",
  cirrus_black9: "#3E424E",
  cirrus_black10: "#373A43",
  cirrus_black11: "#161616",
  cirrus_black12: "#5c6170",
  cirrus_black_dark: "#000",
  cirrus_scrollbar_thumb: "#D9D9D9",
  cirrus_tooltip_error: "#FFE3E8",
  cirrus_toggle_disable: "#8C94A4",

  shadow_black: "#00000040",
  shadow_black_2: "#0000000f",
  footer_border: "#989898",
  cirrus_orange_dark: "#FFCEA9",
  cirrus_orange_light: "#FFF4EB",

  cirrus_orange_dark1: "#E0690F",
  cirrus_orange_dark2: "#B4570C",
  cirrus_orange_light1: " #FFF5EE",
  cirrus_violet: "#736BDE",
  cirrus_violet1: "#6963DC",
  cirrus_violet_light: "#FAFAFF",
  cirrus_violet_light1: "#FCF9FF",
  cirrus_olive_green: "#8A8905",
  cirrus_olive_green1: "#777704",
  cirrus_olive_green_light: "#FFFFDF",
  cirrus_green_light: "#EFFFEB",
  cirrus_green1: "#1A8100",
  cirrus_red_mild: "#FFF0F3",
  cirrus_brown: "#603913",
  cirrus_red_mild1: "#FFEDED",
  cirrus_red_mild2: "#FFDADA",
  cirrus_green2: "#145F01",
  cirrus_peach_light: "#FCF3EB",
  cirrus_rose_tint: "#FC5C7D1A",
  cirrus_violet_overlay: "#9E94FA1F",

  cirrus_coach_metrics_caseloads_vs_engagement_value: "#BED9FF",
  cirrus_coach_metrics_caseloads_vs_engagement_circle: "#204F7D",
  cirrus_coach_metrics_caseloads_vs_engagement: "#32679F",
  cirrus_coach_metrics_caseloads_vs_engagement_2: "#0F3154",

  cirrus_coach_metrics_average_time_spent_value: "#D3CCFA",
  cirrus_coach_metrics_average_time_spent_circle: "#5F52A8",
  cirrus_coach_metrics_average_time_spent: "#7A6BC7",
  cirrus_coach_metrics_average_time_spent_2: "#4F4296",

  cirrus_coach_metrics_coach_activities_value: "#FFD0C5",
  cirrus_coach_metrics_coach_activities_circle: "#CE471B",
  cirrus_coach_metrics_coach_activities: "#ED7111",
  cirrus_coach_metrics_coach_activities_2: "#B22123",

  cirrus_coach_metrics_participants_landed_value: "#B5FFEB",
  cirrus_coach_metrics_participants_landed_circle: "#2E9376",
  cirrus_coach_metrics_participants_landed: "#4EC1A3",
  cirrus_coach_metrics_participants_landed_2: "#1A755A",
};

export const BREAKPOINTS = {
  // Mobile
  S_MOBILE: "360px",
  MID_MOBILE: "414px",
  LARGE_MOBILE: "480px",
  MOBILE: "575px", // general mobile alias

  // Tablet
  TABLET_PORTRAIT: "768px",
  TABLET_MID: "899px",
  TABLET_LANDSCAPE: "1024px",

  // Laptops
  SMALL_LAPTOP: "1280px",
  LAPTOP: "1366px",

  // Desktop
  DESKTOP: "1440px",
  LARGE_DESKTOP: "1920px",
};

export const THEME = {
  fontFamilies: {
    primary: "Inter",
    secondary: "Arial",
  },
  fontWeights: {
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
    extraBold: 800,
    black: 900,
  },
  fontSizes: {
    800: "32px", // for header
    700: "24px", // for h1
    650: "22px", // for (Mobile - h1)
    600: "20px", // for h2
    500: "18px", // for h4, body text | (Mobile - h2)
    400: "16px", // for h3, p, div, etc | (Mobile - h4)
    300: "14px", // for span or small text
  },
};

// Type for BREAKPOINTS
type Breakpoints = typeof BREAKPOINTS;
type BreakpointKey = keyof Breakpoints;

// The responsive helper function
export const responsive = Object.keys(BREAKPOINTS).reduce(
  (acc, label) => {
    acc[label as BreakpointKey] = (...args: Parameters<typeof css>) => css`
      @media (max-width: ${BREAKPOINTS[label as BreakpointKey]}) {
        ${css(...args)}
      }
    `;
    return acc;
  },
  {} as Record<BreakpointKey, (...args: Parameters<typeof css>) => CSSProp>,
);

// The responsive helper function for explicitly targeting exceptional range 768px - 899px
export const tabletMidRange = (...args: Parameters<typeof css>) => css`
  @media (min-width: ${BREAKPOINTS.TABLET_PORTRAIT}) and (max-width: ${BREAKPOINTS.TABLET_MID}) {
    ${css(...args)}
  }
`;

// The responsive helper function for explicitly targeting exceptional range 900px - 1199px
export const tabletAboveRange = (...args: Parameters<typeof css>) => css`
  @media (min-width: 900px) and (max-width: 1199px) {
    ${css(...args)}
  }
`;

export const GlobalCSSVariables = createGlobalStyle`
  :root {
    --primary_light_blue: ${COLORS.primary_light_blue};
    --color-border: ${COLORS.cirrus_black4};
    --color-highlight: ${COLORS.primary_light_blue2}; 
    --color-selected: ${COLORS.primary_light_blue2};
    --font-size-h3: ${THEME.fontSizes[400]};
    --font-weight-h3: ${THEME.fontWeights["regular"]};
    --font-family-primary: ${THEME.fontFamilies.primary};
    --cirrus_black: ${COLORS.cirrus_black};
  }
`;
